import { watch } from 'vue'
import { useAuth } from '@/stores/auth'
import { useGameState } from '@/stores/gameState'
import { useRouter } from 'vue-router'
import { removeAuthToken, removePlayerOpenedResults } from '@/helpers/storage'
import { decodeJwtPlayerInfo } from '@/helpers/jwt'
import { getPlayer } from '@/api/getPlayer'

export function useAuthSession() {
  const authStore = useAuth()
  const gameState = useGameState()
  const router = useRouter()

  const clearAuthState = () => {
    removeAuthToken()
    authStore.token = null
    authStore.player = null
    authStore.role = null
  }

  const processAuthToken = async (token: string) => {
    const playerInfo = decodeJwtPlayerInfo(token)
    authStore.role = playerInfo?.role || null

    const player = await getPlayer()
    authStore.player = player
  }

  const logout = async (redirectPath = '/home2') => {
    authStore.token = null
    authStore.player = null
    authStore.role = null
    authStore.status = 'idle'
    authStore.clearAuthInvalid()
    authStore.clearForbidden()

    removeAuthToken()
    removePlayerOpenedResults()

    gameState.clean()

    try {
      await router.push(redirectPath)
    } catch (navError) {
      // Fallback: reload page if navigation fails
      console.warn('Navigation failed during logout, reloading page:', navError)
      window.location.href = redirectPath
    }
  }

  const redirectToLogin = async (loginPath = '/login2') => {
    authStore.clearForbidden()

    try {
      await router.push(loginPath)
    } catch (navError) {
      // Fallback: reload page if navigation fails
      console.warn('Navigation failed during login redirect, reloading page:', navError)
      window.location.href = loginPath
    }
  }

  const watchAuthErrors = () => {
    // Watch for 401 errors (invalid token) - trigger full logout
    watch(
      () => authStore.isAuthInvalid,
      (isInvalid) => {
        if (isInvalid) {
          console.warn('Authentication token invalid, logging out...')
          logout()
        }
      }
    )

    // Watch for 403 errors (forbidden access) - redirect to login
    watch(
      () => authStore.isForbidden,
      (isForbidden) => {
        if (isForbidden) {
          console.warn('Access forbidden, redirecting to login...')
          redirectToLogin()
        }
      }
    )
  }

  return {
    logout,
    redirectToLogin,
    watchAuthErrors,
    clearAuthState,
    processAuthToken
  }
}
